import { QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { PersistedClient, Persister } from '@tanstack/react-query-persist-client';

const CACHE_BUSTER = 'v1';
const CACHE_KEY = 'koo-query-cache';
const MAX_AGE = 60 * 60_000;

export class ApiError extends Error {
  statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) {
    return false;
  }
  const statusCode =
    error instanceof ApiError
      ? error.statusCode
      : (error as { statusCode?: number })?.statusCode;
  if (statusCode && statusCode >= 400 && statusCode < 500) {
    return false;
  }
  return true;
}

function retryDelay(attemptIndex: number): number {
  return 1000 * 2 ** attemptIndex + Math.floor(Math.random() * 1000);
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      networkMode: 'online',
      retry: shouldRetry,
      retryDelay,
      cacheTime: 30 * 60_000,
      staleTime: 30_000,
    },
  },
});

function createSessionStoragePersister(): Persister {
  if (typeof window === 'undefined') {
    return {
      persistClient: async () => undefined,
      restoreClient: async () => undefined,
      removeClient: async () => undefined,
    };
  }

  return createSyncStoragePersister({
    storage: window.sessionStorage,
    key: CACHE_KEY,
  });
}

export const queryPersister = createSessionStoragePersister();

export const persistOptions = {
  persister: queryPersister,
  maxAge: MAX_AGE,
  buster: CACHE_BUSTER,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: { state: { status: string }; queryKey: readonly unknown[] }) => {
      if (query.state.status !== 'success') {
        return false;
      }
      const rootKey = query.queryKey[0];
      if (rootKey === 'auth') {
        return false;
      }
      return true;
    },
  },
};

export type { PersistedClient };
